import React, { useState } from 'react';
import { useQuery } from 'react-query';
import { 
  Paper, List, ListItem, ListItemText, Divider, Grid,
  Chip, Button, Stack, Typography, Box, Skeleton, Avatar, Pagination 
} from '@mui/material';
import { VisibilityOutlined, CompareArrowsOutlined, PersonOutline, RestaurantOutlined, HistoryToggleOffOutlined } from '@mui/icons-material';

import { fetchAssessmentResponses } from '../../../../api/services/Assessment/InitalAssessment';
import { QK } from '../../../../base/qk';
import { AssessmentDetailsModal } from '../../../CarePlan/InitialAssessment/Modal';

const AssessmentHistory = ({ clientID, typeId }) => {
  const [page, setPage] = useState(1);
  const [selectedResponse, setSelectedResponse] = useState(null);
  const [open, setOpen] = useState(false);

  const { data, isLoading, isError } = useQuery(
    [QK.initAssessmentResponses, clientID, typeId, page],
    () => fetchAssessmentResponses(clientID, typeId, page),
    { keepPreviousData: true }
  );

  const responses = data?.results || [];
  const totalPages = data?.count ? Math.ceil(data.count / 10) : 1;

  const handleOpenModal = (resp) => {
    setSelectedResponse(resp);
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
    setSelectedResponse(null);
  };

  if (isLoading) return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      {[1, 2, 3].map((i) => <Skeleton key={i} variant="rounded" height={140} sx={{ borderRadius: '16px' }} />)}
    </Box>
  );

  if (isError || responses.length === 0) return (
    <Box sx={{ textAlign: 'center', py: 10, bgcolor: '#f9fafb', borderRadius: '24px', border: '1px dashed #eaecf0' }}>
      <HistoryToggleOffOutlined sx={{ fontSize: 48, color: '#98a2b3', mb: 2 }} />
      <Typography variant="h6" fontWeight={700} color="#101828">No Assessment History</Typography>
      <Typography variant="body2" color="#667085">There are no recorded assessments for this client yet.</Typography>
    </Box>
  );

  return (
    <Box>
      <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 3 }}>
        <Typography variant="subtitle2" color="#475467" fontWeight={600}>
          Showing <strong>{responses.length}</strong> of <strong>{data?.count || 0}</strong> assessments
        </Typography>
        {responses.length > 1 && (
          <Button 
            startIcon={<CompareArrowsOutlined />} 
            size="small" 
            variant="outlined"
            sx={{ 
              textTransform: 'none', 
              fontWeight: 700, 
              borderRadius: '8px',
              borderColor: '#d0d5dd',
              color: '#344054'
            }}
          >
            Compare Iterations
          </Button>
        )}
      </Stack>

      <Paper variant="outlined" sx={{ borderRadius: '16px', overflow: 'hidden', border: '1px solid #eaecf0', bgcolor: '#fff' }}>
        <List disablePadding>
          {responses.map((resp, idx) => (
            <React.Fragment key={resp.id}>
              <ListItem 
                sx={{ 
                  py: 3, px: 3, 
                  transition: '0.2s',
                  '&:hover': { bgcolor: '#fcfcfd' },
                  borderLeft: resp.is_latest ? '4px solid #079455' : '4px solid transparent'
                }}
                secondaryAction={
                  <Button 
                    startIcon={<VisibilityOutlined />} 
                    variant="contained"
                    size="small"
                    onClick={() => handleOpenModal(resp)}
                    sx={{ 
                      borderRadius: '8px', bgcolor: '#fff', color: '#344054', 
                      border: '1px solid #d0d5dd', boxShadow: 'none', textTransform: 'none', fontWeight: 700,
                      '&:hover': { bgcolor: '#f9fafb', border: '1px solid #d0d5dd', boxShadow: 'none' }
                    }}
                  >
                    View Details
                  </Button>
                }
              >
                <ListItemText 
                  primary={
                    <Stack direction="row" spacing={1.5} alignItems="center" sx={{ mb: 2 }}>
                      <Typography variant="h6" fontWeight={800} sx={{ color: '#101828', fontSize: '0.95rem' }}>
                        Clinical Review Cycle #{resp.review_cycle}
                      </Typography>
                      {resp.is_latest && (
                        <Chip 
                          label="Latest Update" 
                          size="small" 
                          sx={{ bgcolor: '#ecfdf3', color: '#067647', fontWeight: 700, fontSize: '10px', height: '20px' }} 
                        />
                      )}
                    </Stack>
                  }
                  secondary={
                    <Box>
                      <Grid container spacing={2}>
                        <Grid item xs={12} sm={4}>
                          <Stack direction="row" spacing={1} alignItems="center">
                            <Avatar sx={{ width: 24, height: 24, bgcolor: '#f9fafb', border: '1px solid #eaecf0' }}>
                              <PersonOutline sx={{ fontSize: 14, color: '#667085' }} />
                            </Avatar>
                            <Box>
                              <Typography variant="caption" color="#667085" sx={{ display: 'block', lineHeight: 1 }}>Assessed by</Typography>
                              <Typography variant="body2" fontWeight={700} color="#344054">{resp.created_by_name || 'System'}</Typography>
                            </Box>
                          </Stack>
                        </Grid>
                        
                        <Grid item xs={12} sm={4}>
                          <Stack direction="row" spacing={1} alignItems="center">
                            <Avatar sx={{ width: 24, height: 24, bgcolor: '#f9fafb', border: '1px solid #eaecf0' }}>
                              <HistoryToggleOffOutlined sx={{ fontSize: 14, color: '#667085' }} />
                            </Avatar>
                            <Box>
                              <Typography variant="caption" color="#667085" sx={{ display: 'block', lineHeight: 1 }}>Assessment Date</Typography>
                              <Typography variant="body2" fontWeight={700} color="#344054">
                                {new Date(resp.created_at).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })}
                              </Typography>
                            </Box>
                          </Stack>
                        </Grid>

                        <Grid item xs={12} sm={4}>
                           <Stack direction="row" spacing={1} alignItems="center">
                              <Avatar sx={{ width: 24, height: 24, bgcolor: '#f9fafb', border: '1px solid #eaecf0' }}>
                                <RestaurantOutlined sx={{ fontSize: 14, color: '#667085' }} />
                              </Avatar>
                              <Box>
                                <Typography variant="caption" color="#667085" sx={{ display: 'block', lineHeight: 1 }}>Plan Status</Typography>
                                <Typography variant="body2" fontWeight={700} color="#344054">
                                  {resp.is_latest ? 'Active' : 'Superseded'}
                                </Typography>
                              </Box>
                           </Stack>
                        </Grid>
                      </Grid>
                    </Box>
                  }
                />
              </ListItem>
              {idx < responses.length - 1 && <Divider sx={{ borderColor: '#eaecf0' }} />}
            </React.Fragment>
          ))}
        </List>
      </Paper>

      {totalPages > 1 && (
        <Box sx={{ mt: 4, display: 'flex', justifyContent: 'center' }}>
          <Pagination 
            count={totalPages} 
            page={page} 
            onChange={(e, v) => setPage(v)} 
            color="primary" 
            shape="rounded"
            size="medium"
            sx={{
              '& .MuiPaginationItem-root': { fontWeight: 700, borderRadius: '8px' },
              '& .Mui-selected': { bgcolor: '#101828 !important', color: '#fff' }
            }}
          />
        </Box>
      )}

      <AssessmentDetailsModal
        open={open} 
        handleClose={handleClose} 
        selectedResponse={selectedResponse} 
      />
    </Box>
  );
};

export default AssessmentHistory;